import React, { useEffect, useState, useCallback } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import DvsEvaluationService, {
  PaperForEvaluation,
} from "../../_services/DvsEvaluationService";
import PdfAnnotationViewer from "./components/PdfAnnotationViewer";
import OnlinePaperView from "./components/OnlinePaperView";
import MarksEntryPanel from "./components/MarksEntryPanel";
import ReportIssueModal from "./components/ReportIssueModal";
import SubmitConfirmationModal from "./components/SubmitConfirmationModal";
import "./dvs-evaluation.css";

const DvsEvaluationPage: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const location = useLocation();
  const history = useHistory();

  const evaluatorType =
    Number(new URLSearchParams(location.search).get("evaluatorType")) || 1;

  const [paper, setPaper] = useState<PaperForEvaluation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<number, string>
  >({});
  const [showReportModal, setShowReportModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [hasMorePending, setHasMorePending] = useState(false);
  const [viewMode, setViewMode] = useState<"pdf" | "online">("pdf");

  const loadPaper = useCallback(async () => {
    setLoading(true);
    setError("");
    setValidationErrors({});
    try {
      const data = await DvsEvaluationService.getNextPaper(
        Number(testId),
        evaluatorType,
      );
      if (!data) {
        setError("No pending papers for this test.");
      }
      setPaper(data);
    } catch (err: any) {
      setError(err?.message || "Failed to load paper");
    } finally {
      setLoading(false);
    }
  }, [testId, evaluatorType]);

  useEffect(() => {
    loadPaper();
  }, [loadPaper]);

  const handleMarksChange = useCallback(
    (attemptId: number, field: "marks" | "remarks", value: string) => {
      if (!paper) return;
      setPaper((prev) => {
        if (!prev) return prev;
        const sections = prev.sections.map((sec) => ({
          ...sec,
          questions: sec.questions.map((q) => {
            if (q.attemptId !== attemptId) return q;
            if (field === "marks") {
              return { ...q, obtainedMarks: value === "" ? null : Number(value) };
            }
            return { ...q, remarks: value || null };
          }),
        }));
        return { ...prev, sections };
      });
      // Clear validation error for this field
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[attemptId];
        return next;
      });
    },
    [paper],
  );

  const validateMarks = useCallback((): boolean => {
    if (!paper) return false;
    const errors: Record<number, string> = {};
    for (const sec of paper.sections) {
      if (sec.showTotalMarksOnly) continue;
      for (const q of sec.questions) {
        if (q.obtainedMarks === null || q.obtainedMarks === undefined) {
          errors[q.attemptId] = "Required";
        } else if (q.obtainedMarks < 0) {
          errors[q.attemptId] = "Cannot be negative";
        } else if (q.obtainedMarks > q.maxMarks) {
          errors[q.attemptId] = `Max ${q.maxMarks}`;
        }
      }
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [paper]);

  const handleSubmit = useCallback(async () => {
    if (!paper) return;
    if (!validateMarks()) return;

    setSubmitting(true);
    try {
      const marks =
        paper.evaluatorType === 2
          ? paper.sections
              .filter((sec) => !sec.showTotalMarksOnly)
              .flatMap((sec) =>
                sec.questions.map((q) => ({
                  attemptId: q.attemptId,
                  obtainedMarks: q.obtainedMarks ?? 0,
                })),
              )
          : undefined;

      const result = await DvsEvaluationService.submitEvaluation({
        mappingId: paper.mappingId,
        evaluatorType: paper.evaluatorType,
        marks,
      });

      if (!result.success) {
        if (result.errors) {
          setValidationErrors(result.errors as any);
        }
        setError(result.error || "Submission failed");
        return;
      }

      setHasMorePending(result.hasMorePending);
      setShowSubmitModal(true);
    } catch (err: any) {
      setError(err?.message || "Failed to submit evaluation");
    } finally {
      setSubmitting(false);
    }
  }, [paper, validateMarks]);

  const handleReportIssue = useCallback(
    async (comment: string) => {
      if (!paper) return;
      const result = await DvsEvaluationService.reportIssue(
        paper.mappingId,
        comment,
      );
      setShowReportModal(false);
      setHasMorePending(result.hasMorePending);
      setShowSubmitModal(true);
    },
    [paper],
  );

  const handleSkip = useCallback(async () => {
    if (!paper) return;
    try {
      await DvsEvaluationService.skipPaper(paper.mappingId, paper.evaluatorType);
    } catch {}
    await loadPaper();
  }, [paper, loadPaper]);

  const handleContinue = useCallback(async () => {
    setShowSubmitModal(false);
    await loadPaper();
  }, [loadPaper]);

  const handleExit = useCallback(() => {
    history.push("/dvs-evaluation");
  }, [history]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "60px" }}>
        <Spinner role="status" animation="border" variant="primary" />
        <p style={{ marginTop: "10px" }}>Loading paper...</p>
      </div>
    );
  }

  if (error && !paper) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <div style={{ color: "red", marginBottom: "16px" }}>{error}</div>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => history.push("/dvs-evaluation")}
        >
          Back to Tests
        </button>
      </div>
    );
  }

  if (!paper) return null;

  const hasPdf = !!paper.testPaperUrl;
  const hasOnline = paper.hasOnlineAnswers;
  // Show toggle only when BOTH views are available
  const showSwitchButton = hasPdf && hasOnline;
  // When no PDF, fall back to the questions-list view regardless of online answers —
  // matches the production Java behavior of rendering question statements for offline tests.
  const effectiveViewMode = !hasPdf ? "online" : viewMode;

  return (
    <div
      className="dvs-eval-root"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 60px)",
        overflow: "hidden",
      }}
    >
      {/* Back button header */}
      <div className="dvs-eval-topbar">
        <button
          className="dvs-eval-topbar__back"
          onClick={() => history.push("/dvs-evaluation")}
        >
          <span>←</span> Back to DVS Listing
        </button>
        <div className="dvs-eval-topbar__meta">
          <strong>Test ID: {testId}</strong>
          {paper?.testName && (
            <>
              {" · "}
              <span>{paper.testName}</span>
            </>
          )}
          {paper?.showStudentName && paper?.studentName && (
            <>
              {" · "}
              <span style={{ color: "var(--text-faint)" }}>{paper.studentName}</span>
            </>
          )}
          {!paper && (
            <>
              {" · "}
              <span style={{ color: "var(--text-faint)" }}>Loading…</span>
            </>
          )}
        </div>
      </div>

      {/* Main content area */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Left panel - PDF viewer / Online paper ~67% */}
        <div style={{ flex: 2, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {/* Switch to Online/Offline Paper button */}
          {showSwitchButton && (
            <div
              style={{
                background: "#f0ad4e",
                color: "var(--text-on-brand)",
                textAlign: "center",
                padding: "8px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "0.9em",
              }}
              onClick={() => setViewMode(viewMode === "pdf" ? "online" : "pdf")}
            >
              {viewMode === "pdf" ? "Switch To Online Paper" : "Switch To Offline Paper"}
            </div>
          )}

        {effectiveViewMode === "pdf" ? (
          hasPdf ? (
            <PdfAnnotationViewer
              mappingId={paper.mappingId}
              testPaperUrl={paper.testPaperUrl || ""}
              employeeId={paper.employeeId}
            />
          ) : (
            <div className="dvs-eval-empty">
              <div className="dvs-eval-empty__msg">
                No scanned paper available for this student.
              </div>
              <div className="dvs-eval-empty__actions">
                <button
                  className="dvs-eval-btn dvs-eval-btn--report"
                  onClick={() => setShowReportModal(true)}
                >
                  Report Issue
                </button>
                <button
                  className="dvs-eval-btn dvs-eval-btn--submit"
                  onClick={handleSkip}
                >
                  Skip to Next Paper
                </button>
                <button
                  className="dvs-eval-btn dvs-eval-btn--ghost"
                  onClick={() => history.push("/dvs-evaluation")}
                >
                  Back to Tests
                </button>
              </div>
            </div>
          )
        ) : (
          <OnlinePaperView sections={paper.sections} />
        )}
        </div>

        {/* Right panel - Marks entry ~33% */}
        <div style={{ flex: 1, minWidth: "300px", display: "flex", flexDirection: "column" }}>
          <MarksEntryPanel
            paper={paper}
            onMarksChange={handleMarksChange}
            onSubmit={handleSubmit}
            onReportIssue={() => setShowReportModal(true)}
            submitting={submitting}
            validationErrors={validationErrors}
          />
        </div>
      </div>

      {/* Error toast */}
      {error && paper && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            background: "var(--status-danger)",
            color: "var(--text-on-brand)",
            padding: "10px 16px",
            borderRadius: "6px",
            fontSize: "0.85em",
            zIndex: 9999,
            cursor: "pointer",
          }}
          onClick={() => setError("")}
        >
          {error}
        </div>
      )}

      <ReportIssueModal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        onSubmit={handleReportIssue}
      />

      <SubmitConfirmationModal
        show={showSubmitModal}
        hasMorePending={hasMorePending}
        onContinue={handleContinue}
        onExit={handleExit}
      />
    </div>
  );
};

export default DvsEvaluationPage;
